/**
 * useSOLPrice Hook
 *
 * Fetches SOL/USD price via the sol-price edge function (Pyth Hermes oracle feed).
 * Provides both hook-based and imperative access for use in React components
 * and the TradingView datafeed.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import BN from 'bn.js';
import { supabase } from '@/integrations/supabase/client';

const FALLBACK_PRICE = 200;
const CACHE_TIME = 30 * 1000; // 30 seconds

// Global cache for non-hook usage
let priceCache: { price: number; timestamp: number } | null = null;

export interface SOLPriceResult {
  price: number;
  isFallback: boolean;
}

async function fetchSOLPrice(): Promise<SOLPriceResult> {
  try {
    const { data, error } = await supabase.functions.invoke('sol-price');

    if (error) {
      throw new Error(`Edge function error: ${error.message}`);
    }

    const solPrice = data?.price;

    if (typeof solPrice !== 'number' || solPrice <= 0) {
      throw new Error('Invalid SOL price from edge function');
    }

    // Update global cache
    priceCache = {
      price: solPrice,
      timestamp: Date.now(),
    };

    return { price: solPrice, isFallback: false };
  } catch (error) {
    console.warn('[useSOLPrice] Failed to fetch price:', error);

    // Return cached price if available
    if (priceCache && Date.now() - priceCache.timestamp < CACHE_TIME * 2) {
      return { price: priceCache.price, isFallback: true };
    }

    return { price: FALLBACK_PRICE, isFallback: true };
  }
}

/**
 * React hook to get SOL/USD price with automatic refresh.
 */
export function useSOLPrice() {
  return useQuery<SOLPriceResult>({
    queryKey: ['sol-price'],
    queryFn: fetchSOLPrice,
    staleTime: CACHE_TIME,
    gcTime: 5 * 60 * 1000, // Tier 2
    retry: 1,
    refetchOnWindowFocus: false,
    refetchInterval: CACHE_TIME,
  });
}

/**
 * Hook to manually refresh SOL price.
 */
export function useRefreshSOLPrice() {
  const queryClient = useQueryClient();
  return () => {
    priceCache = null;
    queryClient.invalidateQueries({ queryKey: ['sol-price'] });
  };
}

/**
 * Get SOL price synchronously (from cache or fallback).
 * Use for non-hook contexts like the datafeed.
 */
export function getSOLPriceSync(): number {
  if (priceCache && Date.now() - priceCache.timestamp < CACHE_TIME) {
    return priceCache.price;
  }
  return priceCache?.price ?? FALLBACK_PRICE;
}

/**
 * Fetch SOL price asynchronously (for use outside React components).
 * Manages caching automatically.
 */
export async function getSOLPrice(): Promise<number> {
  // Return cached price if still valid
  if (priceCache && Date.now() - priceCache.timestamp < CACHE_TIME) {
    return priceCache.price;
  }

  const result = await fetchSOLPrice();
  return result.price;
}

/**
 * Initialize SOL price fetching (call early in app startup).
 * Sets up background refresh every 30 seconds.
 */
let initializationStarted = false;
export function initSOLPriceFetching(): void {
  if (initializationStarted) return;
  initializationStarted = true;

  // Fetch immediately
  fetchSOLPrice();

  // Set up background refresh
  setInterval(() => {
    fetchSOLPrice();
  }, CACHE_TIME);

}

// Legacy exports for backward compatibility
export function useSolPrice() {
  return useSOLPrice();
}

/**
 * Convert lamports (BN) to USD value
 */
export function lamportsToUsd(lamports: BN, solPrice: number): number {
  const solAmount = lamports.toNumber() / 1e9;
  return solAmount * solPrice;
}

/**
 * Convert lamports (BN) to SOL
 */
export function lamportsToSol(lamports: BN): number {
  return lamports.toNumber() / 1e9;
}

/**
 * Format USD value with appropriate precision
 */
export function formatUsd(usd: number): string {
  if (usd >= 1000) {
    return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  if (usd >= 1) {
    return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}
